import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  Tabs,
  Tab,
  IconButton,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Input,
} from '@mui/material';
import {
  ArrowBack,
  Logout,
  Add,
  Edit,
  Delete,
  CloudUpload,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, {
  departmentAPI,
  systemConfigAPI,
  attendanceAPI,
  holidayGroupAPI,
  holidayAPI,
  employeeGroupAPI,
  salaryAdvanceAPI,
} from '../services/api';
import JSZip from 'jszip';
import { calculateRows } from './payroll/utils/salaryCalculations';
import { groupRowsByEmployeeGroup } from './payroll/config/groupingConfig';
import { buildSalaryStatementExcelBlob } from './payroll/utils/salaryStatementExcel';
import { buildSalaryAdvanceExcelBlob } from './salaryadvance/utils/salaryAdvanceExcel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmployeeManagement from '../components/admin/EmployeeManagement';
import HolidayManagement from '../components/admin/HolidayManagement';
import BankManagement from '../components/admin/BankManagement';
import UsersTab from '../components/admin/UsersTab';
import RolesTab from '../components/admin/RolesTab';

const AdminDashboard = () => {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Every Admin Portal sub-tab, gated by its own permission key - see
  // backend/app/core/permissions.py for the matching catalog. Built inside
  // the component body (not at module scope) since these tab components are
  // `const` declarations further down this same file - referencing them
  // before their own declaration line runs would hit the temporal dead zone.
  // Filtered down to whatever the logged-in role actually grants before
  // rendering, so the tab index used by MUI's <Tabs> always lines up with
  // what's visible.
  const ADMIN_SUB_TABS = [
    { permission: 'admin.system_config', label: 'System Config', component: SystemConfigTab },
    { permission: 'admin.departments', label: 'Departments', component: DepartmentsTab },
    { permission: 'admin.banks', label: 'Banks', component: BankManagement },
    { permission: 'admin.employees', label: 'Employees', component: EmployeeManagement },
    { permission: 'admin.holidays', label: 'Holidays', component: HolidayManagement },
    { permission: 'admin.holiday_groups', label: 'Holiday Groups', component: HolidayGroupsTab },
    { permission: 'attendance.upload', label: 'Upload Attendance', component: AttendanceUploadTab },
    { permission: 'attendance.cleanup', label: 'Data Cleanup', component: DataCleanupTab },
    { permission: 'admin.pay_groups', label: 'Pay Groups', component: GroupsTab },
  ];

  const visibleTabs = ADMIN_SUB_TABS.filter((t) => hasPermission(t.permission));
  // Users/Roles management is deliberately NOT a checkbox permission - see
  // app/core/permissions.py - so it's gated by isAdmin() specifically here
  // too, not folded into the filter above.
  if (isAdmin()) {
    visibleTabs.push({ label: 'Users', component: UsersTab });
    visibleTabs.push({ label: 'Roles', component: RolesTab });
  }

  const ActiveTabComponent = visibleTabs[tabValue]?.component;

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            Admin Portal
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {user?.full_name}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {visibleTabs.map((t) => (
              <Tab key={t.label} label={t.label} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {ActiveTabComponent ? (
              <ActiveTabComponent />
            ) : (
              <Typography color="text.secondary">No sections available for your role.</Typography>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// System Config Tab
const SystemConfigTab = () => {
  const [configs, setConfigs] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await systemConfigAPI.getAll();
      const configMap = {};
      response.data.forEach(config => {
        configMap[config.config_key] = config.config_value;
      });
      setConfigs(configMap);
    } catch (error) {
      setMessage('Failed to load configurations');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLoading(true);
    try {
      await systemConfigAPI.uploadLogo(logoFile);
      setMessage('Logo uploaded successfully');
      setLogoFile(null);
    } catch (error) {
      setMessage('Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = async (key, value) => {
    try {
      await systemConfigAPI.update({ config_key: key, config_value: value });
      setMessage('Configuration updated successfully');
      loadConfigs();
    } catch (error) {
      setMessage('Failed to update configuration');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        System Configuration
      </Typography>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Available Punches (comma-separated)
        </Typography>
        <TextField
          fullWidth
          value={configs.available_punches || ''}
          onChange={(e) => setConfigs({ ...configs, available_punches: e.target.value })}
          onBlur={() => handleConfigUpdate('available_punches', configs.available_punches)}
          placeholder="e.g., 1,2,3,4,5,6"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Begin Time of Day
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A punch before this time counts toward the previous day's attendance, not its own calendar
          date - e.g. with 03:00, a 1am punch on the 15th belongs to the 14th. Set to 00:00 to disable.
        </Typography>
        <TextField
          type="time"
          value={configs.day_cycle_start_time || '03:00'}
          onChange={(e) => setConfigs({ ...configs, day_cycle_start_time: e.target.value })}
          onBlur={() => handleConfigUpdate('day_cycle_start_time', configs.day_cycle_start_time)}
          sx={{ width: 160 }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Upload Application Logo
        </Typography>
        <Input
          type="file"
          onChange={(e) => setLogoFile(e.target.files[0])}
          inputProps={{ accept: 'image/*' }}
        />
        <Button
          variant="contained"
          onClick={handleLogoUpload}
          disabled={!logoFile || loading}
          sx={{ ml: 2 }}
          startIcon={<CloudUpload />}
        >
          {loading ? 'Uploading...' : 'Upload Logo'}
        </Button>
      </Box>
    </Box>
  );
};

// Departments Tab
const DepartmentsTab = () => {
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    dept_name: '',
    number_of_punch: 2,
    full_day_threshold_minutes: 480,
    half_day_threshold_minutes: 240,
    absent_threshold_minutes: 120,
    total_working_hours: 480,
    overtime_threshold_minutes: 40,
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await departmentAPI.getAll();
      setDepartments(response.data);
    } catch (error) {
      setMessage('Failed to load departments');
    }
  };

  const handleSubmit = async () => {
    if (!formData.dept_name || !formData.dept_name.trim()) {
      setMessage('Department Name is mandatory');
      return;
    }
    if (!formData.full_day_threshold_minutes || formData.full_day_threshold_minutes <= 0) {
      setMessage('Full Day Threshold (minutes) is mandatory and must be greater than 0');
      return;
    }
    if (!formData.half_day_threshold_minutes || formData.half_day_threshold_minutes <= 0) {
      setMessage('Half Day Threshold (minutes) is mandatory and must be greater than 0');
      return;
    }
    if (!formData.absent_threshold_minutes || formData.absent_threshold_minutes <= 0) {
      setMessage('Absent Threshold (minutes) is mandatory and must be greater than 0');
      return;
    }
    if (!formData.total_working_hours || formData.total_working_hours <= 0) {
      setMessage('Total Working Hours is mandatory and must be greater than 0');
      return;
    }
    if (!formData.overtime_threshold_minutes || formData.overtime_threshold_minutes <= 0) {
      setMessage('Overtime Threshold Minutes is mandatory and must be greater than 0');
      return;
    }
    try {
      if (editing) {
        await departmentAPI.update(editing.dept_id, formData);
        setMessage('Department updated successfully');
      } else {
        await departmentAPI.create(formData);
        setMessage('Department created successfully');
      }
      setOpen(false);
      loadDepartments();
      resetForm();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await departmentAPI.delete(id);
        setMessage('Department deleted successfully');
        loadDepartments();
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Failed to delete department');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      dept_name: '',
      number_of_punch: 2,
      full_day_threshold_minutes: 480,
      half_day_threshold_minutes: 240,
      absent_threshold_minutes: 120,
      total_working_hours: 480,
      overtime_threshold_minutes: 40,
    });
    setEditing(null);
  };

  const handleEdit = (dept) => {
    setEditing(dept);
    setFormData(dept);
    setOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Departments</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Add Department
        </Button>
      </Box>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Full Day (min)</TableCell>
              <TableCell>Half Day (min)</TableCell>
              <TableCell>Absent Threshold (min)</TableCell>
              <TableCell>Total Working Hours (min)</TableCell>
              <TableCell>Overtime Threshold (min)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...departments]
              .sort((a, b) => a.dept_name.localeCompare(b.dept_name))
              .map((dept) => (
              <TableRow key={dept.dept_id}>
                <TableCell>{dept.dept_name}</TableCell>
                <TableCell>{dept.full_day_threshold_minutes}</TableCell>
                <TableCell>{dept.half_day_threshold_minutes}</TableCell>
                <TableCell>{dept.absent_threshold_minutes}</TableCell>
                <TableCell>{dept.total_working_hours}</TableCell>
                <TableCell>{dept.overtime_threshold_minutes}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(dept)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(dept.dept_id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Department' : 'Add Department'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Department Name"
            value={formData.dept_name}
            onChange={(e) => setFormData({ ...formData, dept_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Full Day Threshold (minutes)"
            helperText="At or above this many worked minutes, the day is marked Present"
            value={formData.full_day_threshold_minutes}
            onChange={(e) => setFormData({ ...formData, full_day_threshold_minutes: parseInt(e.target.value) })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Half Day Threshold (minutes)"
            helperText="At or above this many worked minutes (but below Full Day Threshold), the day is marked Half Present"
            value={formData.half_day_threshold_minutes}
            onChange={(e) => setFormData({ ...formData, half_day_threshold_minutes: parseInt(e.target.value) })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Absent Threshold (minutes)"
            helperText="Below this many worked minutes on a genuine complete punch pair, the day is marked Absent instead of Half Present"
            value={formData.absent_threshold_minutes}
            onChange={(e) => setFormData({ ...formData, absent_threshold_minutes: parseInt(e.target.value) })}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Overtime Configurations
          </Typography>
          <TextField
            fullWidth
            required
            margin="normal"
            type="number"
            label="Total Working Hours (minutes)"
            helperText="Default daily working hours (in minutes) used for Overtime calculations - applies to every employee here unless that Employee has their own value set"
            value={formData.total_working_hours}
            onChange={(e) => setFormData({ ...formData, total_working_hours: parseInt(e.target.value) })}
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            required
            margin="normal"
            type="number"
            label="Overtime Threshold Minutes"
            helperText="Default overtime threshold (in minutes) used for Overtime calculations - applies to every employee here unless that Employee has their own value set"
            value={formData.overtime_threshold_minutes}
            onChange={(e) => setFormData({ ...formData, overtime_threshold_minutes: parseInt(e.target.value) })}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Groups Tab (simple named grouping for employees - unrelated to Holiday Groups)
const GroupsTab = () => {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ group_name: '' });
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await employeeGroupAPI.getAll();
      setGroups(response.data);
    } catch (error) {
      setMessage('Failed to load pay groups');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await employeeGroupAPI.update(editing.group_id, formData);
        setMessage('Pay Group updated successfully');
      } else {
        await employeeGroupAPI.create(formData);
        setMessage('Pay Group created successfully');
      }
      setOpen(false);
      loadGroups();
      resetForm();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pay group?')) {
      try {
        await employeeGroupAPI.delete(id);
        setMessage('Pay Group deleted successfully');
        loadGroups();
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Failed to delete pay group');
      }
    }
  };

  const resetForm = () => {
    setFormData({ group_name: '' });
    setEditing(null);
  };

  const handleEdit = (group) => {
    setEditing(group);
    setFormData({ group_name: group.group_name });
    setOpen(true);
  };

  const handleMoveGroup = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= groups.length) return;

    // Optimistic reorder - the same order this list shows is what the
    // Salary Statement webpage (and its Excel export) will follow too.
    const reordered = [...groups];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setGroups(reordered);

    try {
      await employeeGroupAPI.reorder(reordered.map((g) => g.group_id));
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to save order');
      loadGroups(); // re-sync with the server's actual order
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Pay Groups</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Add Pay Group
        </Button>
      </Box>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group, idx) => (
              <TableRow key={group.group_id}>
                <TableCell>{group.group_name}</TableCell>
                <TableCell>
                  <IconButton size="small" disabled={idx === 0} onClick={() => handleMoveGroup(idx, -1)}>
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={idx === groups.length - 1} onClick={() => handleMoveGroup(idx, 1)}>
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => handleEdit(group)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(group.group_id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Pay Group' : 'Add Pay Group'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Pay Group Name"
            value={formData.group_name}
            onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Attendance Upload Tab
const AttendanceUploadTab = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await attendanceAPI.upload(file);
      setMessage(`Success! ${response.data.records_added} records uploaded`);
      setFile(null);
    } catch (error) {
      setMessage('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Attendance Data
      </Typography>

      {message && (
        <Alert severity={message.includes('Success') ? 'success' : 'error'} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          File Format Requirements
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Supported file types:</strong> Excel (.xlsx, .xls) or CSV (.csv)
        </Typography>
        <Typography variant="body2" paragraph>
          The file must contain the following columns:
        </Typography>
        <ul>
          <li><strong>Employee Name</strong>: Name of the employee</li>
          <li><strong>LogDate</strong>: DateTime in format <strong>DD-MM-YYYY HH:MM:SS</strong></li>
        </ul>
        <Typography variant="body2" color="text.secondary">
          Example: 15-01-2025 09:30:00
        </Typography>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          inputProps={{ accept: '.xlsx,.xls,.csv' }}
        />
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          sx={{ ml: 2 }}
          startIcon={<CloudUpload />}
        >
          {uploading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
      </Box>
    </Box>
  );
};

// Holiday Groups Tab
const HolidayGroupsTab = () => {
  const [groups, setGroups] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]);
  const [open, setOpen] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [formData, setFormData] = useState({
    group_name: '',
    description: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadGroups();
    loadHolidays();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await holidayGroupAPI.getAll();
      setGroups(response.data);
    } catch (error) {
      setMessage('Failed to load holiday groups');
    } finally {
      setLoading(false);
    }
  };

  const loadHolidays = async () => {
    try {
      const response = await holidayAPI.getAll();
      setAllHolidays(response.data);
    } catch (error) {
      console.error('Failed to load holidays');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await holidayGroupAPI.update(editing.group_id, formData);
        setMessage('Holiday group updated successfully');
      } else {
        await holidayGroupAPI.create(formData);
        setMessage('Holiday group created successfully');
      }
      setOpen(false);
      loadGroups();
      resetForm();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await holidayGroupAPI.delete(id);
        setMessage('Holiday group deleted successfully');
        loadGroups();
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Failed to delete holiday group');
      }
    }
  };

  const resetForm = () => {
    setFormData({ group_name: '', description: '' });
    setEditing(null);
  };

  const handleEdit = (group) => {
    setEditing(group);
    setFormData({ group_name: group.group_name, description: group.description || '' });
    setOpen(true);
  };

  const handleAssignHolidays = async (group) => {
    setSelectedGroup(group);
    try {
      const response = await holidayGroupAPI.getHolidays(group.group_id);
      setSelectedHolidays(response.data.map(h => h.holiday_id));
      setOpenAssign(true);
    } catch (error) {
      setMessage('Failed to load assigned holidays');
    }
  };

  const handleSaveHolidays = async () => {
    try {
      await holidayGroupAPI.assignHolidays(selectedGroup.group_id, selectedHolidays);
      setMessage(`Successfully assigned ${selectedHolidays.length} holidays to ${selectedGroup.group_name}`);
      setOpenAssign(false);
      loadGroups();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to assign holidays');
    }
  };

  const toggleHoliday = (holidayId) => {
    setSelectedHolidays(prev => 
      prev.includes(holidayId) 
        ? prev.filter(id => id !== holidayId)
        : [...prev, holidayId]
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Holiday Groups</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => { resetForm(); setOpen(true); }}
        >
          Create Group
        </Button>
      </Box>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Group Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Holidays</TableCell>
                <TableCell align="center">Employees</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.group_id}>
                  <TableCell>{group.group_name}</TableCell>
                  <TableCell>{group.description || '-'}</TableCell>
                  <TableCell align="center">{group.holiday_count}</TableCell>
                  <TableCell align="center">{group.employee_count}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleAssignHolidays(group)}
                      sx={{ mr: 1 }}
                    >
                      Assign Holidays
                    </Button>
                    <IconButton size="small" onClick={() => handleEdit(group)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(group.group_id, group.group_name)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Holiday Group' : 'Create Holiday Group'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Group Name"
            value={formData.group_name}
            onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
            placeholder="e.g., Group A - Jan-Apr"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="e.g., Festivals from January to April"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Holidays Dialog */}
      <Dialog open={openAssign} onClose={() => setOpenAssign(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Holidays to {selectedGroup?.group_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select holidays to assign to this group. Selected: {selectedHolidays.length} / {allHolidays.length}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allHolidays.map((holiday) => (
              <FormControlLabel
                key={holiday.holiday_id}
                control={
                  <Switch
                    checked={selectedHolidays.includes(holiday.holiday_id)}
                    onChange={() => toggleHoliday(holiday.holiday_id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{holiday.reason}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(holiday.holiday_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssign(false)}>Cancel</Button>
          <Button onClick={handleSaveHolidays} variant="contained">
            Save Assignments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const DataCleanupTab = () => {
  const today = new Date();
  const currentYear  = today.getFullYear();

  // Earliest selectable year (5 years back)
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

  const [selectedYear,  setSelectedYear]  = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [preview,       setPreview]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [confirmed,     setConfirmed]     = useState(false);
  const [message,       setMessage]       = useState('');
  const [error,         setError]         = useState('');

  const reset = () => {
    setPreview(null);
    setConfirmed(false);
    setMessage('');
    setError('');
  };

  const handlePreview = async () => {
    if (!selectedMonth) return;
    setLoading(true);
    setError('');
    setPreview(null);
    setConfirmed(false);
    try {
      const res = await attendanceAPI.previewCleanup(selectedMonth, selectedYear);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const triggerDownload = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Same "Export All" zip the Salary Statement page's own Export All button
  // builds (Attendance + OT + Salary Statement Excel + Salary Advance) - the
  // Salary Statement PDF is left out here, since that file is a screenshot
  // of the Salary Statement webpage itself (html2canvas on that page's live
  // DOM) and can't be produced from this tab. Every file is independently
  // best-effort, same reasoning as that button: one missing/failing report
  // shouldn't block the backup (or the deletion) over the others.
  const buildExportAllZip = async (month, year) => {
    const monthName = MONTH_NAMES[month - 1];
    const mmyy = `${String(month).padStart(2, '0')}${String(year).slice(-2)}`;
    const monthAbbr = monthName.slice(0, 3);
    const zip = new JSZip();
    const skipped = [];

    try {
      const attendanceResponse = await attendanceAPI.exportRegular(month, year);
      zip.file(`ATS_${mmyy}.xlsx`, attendanceResponse.data);
    } catch {
      skipped.push('Attendance');
    }

    try {
      const otResponse = await attendanceAPI.exportOT(month, year);
      zip.file(`OT_${mmyy}.xlsx`, otResponse.data);
    } catch {
      skipped.push('OT Report');
    }

    try {
      const [generateResponse, groupsResponse] = await Promise.all([
        api.get('/payroll/generate-from-attendance', {
          params: { month, year, groups: JSON.stringify([]) },
        }),
        employeeGroupAPI.getAll().catch(() => ({ data: [] })),
      ]);
      const rawRows = generateResponse.data.data || [];
      const calculatedRows = calculateRows(rawRows, year);
      const rows = calculatedRows.map((row, index) => ({ ...row, globalIndex: index }));
      const paidMap = {};
      rows.forEach((row) => {
        if (row.already_confirmed) paidMap[row.globalIndex] = true;
      });
      const confirmedIndexes = rows.filter((row) => row.already_confirmed).map((row) => row.globalIndex);
      const groupOrder = (groupsResponse.data || []).map((g) => g.group_name);
      const sections = groupRowsByEmployeeGroup(rows, groupOrder);
      const salaryExcelBlob = buildSalaryStatementExcelBlob(sections, monthName, year, paidMap, confirmedIndexes);
      zip.file(`DetailReport_${mmyy}.xlsx`, salaryExcelBlob);
    } catch {
      skipped.push('Salary Statement (Excel)');
    }

    try {
      const advanceResponse = await salaryAdvanceAPI.getReport(month, year);
      const salaryAdvanceExcelBlob = buildSalaryAdvanceExcelBlob(advanceResponse.data, month, year);
      zip.file(`AdvanceData_${mmyy}.xlsx`, salaryAdvanceExcelBlob);
    } catch {
      skipped.push('Salary Advance Report');
    }

    if (Object.keys(zip.files).length === 0) {
      return { skipped, zipBlob: null };
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    triggerDownload(zipBlob, `Reports_${monthAbbr}_${year}.zip`);
    return { skipped, zipBlob };
  };

  const handleDelete = async () => {
    if (!confirmed || !preview) return;
    setDeleting(true);
    setError('');
    const label = `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`;

    try {
      // Step 1: Export All (backup), best-effort - see buildExportAllZip.
      const { skipped, zipBlob } = await buildExportAllZip(selectedMonth, selectedYear);

      // Step 2: Delete
      const res = await attendanceAPI.deleteMonth(selectedMonth, selectedYear);
      const skippedNote = skipped.length > 0 ? ` (could not export: ${skipped.join(', ')})` : '';
      const backupNote = zipBlob ? 'Exported All & deleted' : 'Deleted (export failed entirely)';
      setMessage(
        `${backupNote}${skippedNote} ${res.data.attendance_records_deleted} attendance records and ` +
        `${res.data.adjustment_records_deleted} adjustment records for ${label}.`
      );
      setPreview(null);
      setSelectedMonth('');
      setConfirmed(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Deletion failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Data Cleanup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Permanently delete attendance and adjustment records for a selected month.
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 520 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => { setSelectedYear(e.target.value); reset(); }}
            >
              {years.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => { setSelectedMonth(e.target.value); reset(); }}
            >
              <MenuItem value="">— Select —</MenuItem>
              {MONTH_NAMES.map((name, idx) => {
                const m = idx + 1;
                return (
                  <MenuItem key={m} value={m}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={!selectedMonth || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Preview'}
          </Button>
        </Box>

        {preview && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{MONTH_NAMES[preview.month - 1]} {preview.year}</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                <li>{preview.attendance_records} attendance log records</li>
                <li>{preview.adjustment_records} manual adjustment records</li>
              </ul>
              {preview.leave_records_kept > 0 && (
                <Box sx={{ mt: 1 }}>
                  {preview.leave_records_kept} Leave / Half-Leave record(s) for this month will be{' '}
                  <strong>kept</strong>, not deleted - they feed this year's Leave Availed total on the
                  Employees screen.
                </Box>
              )}
              An <strong>Export All</strong> backup zip (Attendance, OT, Salary Statement, Salary Advance)
              will be downloaded automatically before deletion. The data will then be{' '}
              <strong>permanently deleted</strong> and cannot be recovered.
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  color="error"
                />
              }
              label="I understand this action is irreversible"
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
                disabled={!confirmed || deleting}
                onClick={handleDelete}
              >
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
